import {
  InfiniteData,
  Mutation,
  QueryClient,
  useQueryClient,
} from '@mint-lib/query-client';
import { produce } from 'immer';

import * as API from '../../api/index.js';
import type {
  DataFileServerStepNames,
  TableDataFileValidatedRowsResponse,
  TotalErrorsCountData,
  ValidationSearchParams,
} from '../../api/types.js';
import {
  EnhancedMutationExtraOptions,
  useEnhancedMutation,
} from '../../hooks/useEnhancedMutation.js';
import { MutationKeys, QueryKeys } from '../queryClient.js';
import { useDataFilePreviewQueryOptions } from './preview.js';

// TODO: remove code duplication
type MutationPartial<TVariables, TContext> = {
  /**  partial mutation type with options and state */
  options: Mutation<unknown, unknown, TVariables, TContext>['options'] &
    EnhancedMutationExtraOptions & { meta: { key: string } };
  state: Mutation<unknown, unknown, TVariables, TContext>['state'];
};

export type DiscardRowsMutation = MutationPartial<
  DiscardMutationVariables,
  DiscardMutationContext
>;

export type DiscardMutationVariables = Parameters<
  typeof API.discardTableDataFileRows
>[0] & {
  meta: { selectedRowsIndices: number[] } & (
    | { includeRowIndices: number[] }
    | { excludeRowIndices: number[] }
  );
};

type DiscardMutationContext = {
  searchParams: ValidationSearchParams;
  previousErrorsCount?: TotalErrorsCountData | number;
  previousValidatedRows?: InfiniteData<TableDataFileValidatedRowsResponse>;
};

export const getDiscardDataTableFieldsOptions = ({
  queryClient,
  dataFileId,
  previewQueryKey,
  searchParams,
  stepName,
}: {
  queryClient: QueryClient;
  dataFileId: string;
  previewQueryKey: readonly unknown[];
  searchParams: ValidationSearchParams;
  stepName: Extract<
    DataFileServerStepNames,
    'validation' | 'channel_assignment'
  >;
}) => {
  const totalErrorsCountQueryKey =
    QueryKeys.dataFile.steps[stepName].totalErrorsCount(dataFileId);
  const validatedRowsBaseQueryKey =
    QueryKeys.dataFile.steps[stepName].validatedRows(dataFileId);
  const dynamicValidatedRowsQueryKey = [
    ...validatedRowsBaseQueryKey,
    searchParams,
  ];
  const otherStep =
    stepName === 'validation' ? 'channel_assignment' : 'validation';
  const otherStepValidatedRowsBaseQueryKey =
    QueryKeys.dataFile.steps[otherStep].validatedRows(dataFileId);

  const options: DiscardRowsMutation['options'] = {
    mutationFn: ({ id, payload }) => {
      return API.discardTableDataFileRows({
        id,
        payload,
      });
    },
    onMutate: ({ meta }) => {
      queryClient.cancelQueries({ queryKey: previewQueryKey });
      queryClient.cancelQueries({ queryKey: totalErrorsCountQueryKey });
      queryClient.cancelQueries({
        queryKey: validatedRowsBaseQueryKey,
        exact: false,
      });

      const previousErrorsCount = queryClient.getQueryData<
        TotalErrorsCountData | number
      >(totalErrorsCountQueryKey);
      const previousValidatedRows = queryClient.getQueryData<
        InfiniteData<TableDataFileValidatedRowsResponse>
      >(dynamicValidatedRowsQueryKey);

      if (
        previousValidatedRows === undefined ||
        previousErrorsCount === undefined
      ) {
        throw new Error('No data to discard');
      }

      const previousTotalErrors =
        typeof previousErrorsCount === 'number'
          ? previousErrorsCount
          : previousErrorsCount.totalErrors;

      const rows = previousValidatedRows.pages.flatMap((page) => page.items);
      const selectedRows = meta.selectedRowsIndices.map((index) => rows[index]);

      const newValidatedRows = produce(previousValidatedRows, (draft) => {
        if ('includeRowIndices' in meta) {
          meta.includeRowIndices
            .toSorted(function descOrder(a, b) {
              return b - a;
            })
            .forEach((rowIndex) => {
              const pageNumber = Math.floor(
                rowIndex / searchParams.itemsPerPage,
              );
              const page = draft.pages[pageNumber];
              const pageRowIndex = rowIndex % searchParams.itemsPerPage;
              page.items.splice(pageRowIndex, 1);
            });
        }

        if ('excludeRowIndices' in meta) {
          draft.pages.forEach((page, pageIndex) => {
            page.items = selectedRows.slice(
              pageIndex * page.itemsPerPage,
              page.itemsPerPage,
            );
          });
        }
      });

      const errorsCountInSelectedRows = selectedRows.flatMap((row) =>
        row.validatedCells.filter((cell) => Boolean(cell.error)),
      ).length;
      const newTotalErrors =
        'includeRowIndices' in meta
          ? previousTotalErrors - errorsCountInSelectedRows
          : errorsCountInSelectedRows;
      const newErrorsCount =
        typeof previousErrorsCount === 'number'
          ? newTotalErrors
          : { ...previousErrorsCount, totalErrors: newTotalErrors };
      queryClient.setQueryData(totalErrorsCountQueryKey, newErrorsCount);
      queryClient.setQueryData(dynamicValidatedRowsQueryKey, newValidatedRows);

      queryClient.removeQueries({
        queryKey: validatedRowsBaseQueryKey,
        type: 'inactive',
        // use predicate due to bug in RQ (type: 'inactive' doesn't work)
        predicate(query) {
          const isActiveQuery =
            JSON.stringify(query.queryKey) ===
            JSON.stringify(dynamicValidatedRowsQueryKey);

          return !isActiveQuery;
        },
      });

      return {
        searchParams,
        previousErrorsCount,
        previousValidatedRows,
      };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(
        totalErrorsCountQueryKey,
        context?.previousErrorsCount,
      );
      queryClient.setQueryData(
        dynamicValidatedRowsQueryKey,
        context?.previousValidatedRows,
      );
    },
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: otherStepValidatedRowsBaseQueryKey,
        exact: false,
      });
    },
    meta: {
      key: MutationKeys.discardDataTableFields,
      getErrorsMap: (t) => ({
        default: t('Failed to discard fields'),
      }),
    },
  };

  return options;
};

export const useDiscardValidatedRows = (
  dataFileId: string,
  searchParams: ValidationSearchParams,
) => {
  const queryClient = useQueryClient();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  const mutationOptions = getDiscardDataTableFieldsOptions({
    queryClient,
    dataFileId,
    searchParams,
    stepName: 'validation',
    previewQueryKey,
  });

  return useEnhancedMutation(mutationOptions);
};

export const useDiscardConfiguredRows = (
  dataFileId: string,
  searchParams: ValidationSearchParams,
) => {
  const queryClient = useQueryClient();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  const mutationOptions = getDiscardDataTableFieldsOptions({
    queryClient,
    dataFileId,
    searchParams,
    stepName: 'channel_assignment',
    previewQueryKey,
  });

  return useEnhancedMutation(mutationOptions);
};
